<template>
  <div
    v-if="show"
    ref="warningBox"
    class="v2-price-warning"
    :class="{ 'v2-price-warning--highlight': isHighlighted }"
    tabindex="-1"
  >
    <!-- Triangle pointer -->
    <div class="v2-price-warning__pointer">
      <svg
        width="41"
        height="16"
        viewBox="0 0 41 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M40.2031 16H0.203125C4.04486 16 7.72925 14.4739 10.4458 11.7574L18.7889 3.41421C19.57 2.63317 20.8363 2.63316 21.6173 3.41421L29.9605 11.7574C32.677 14.4739 36.3614 16 40.2031 16Z"
          fill="var(--v2-muted)"
        />
        <path
          d="M0 15.5V15.5C3.91477 15.5 7.67842 13.9888 10.5061 11.2814L18.8169 3.32429C19.5903 2.58377 20.8097 2.58377 21.5831 3.32429L29.8939 11.2814C32.7216 13.9888 36.4852 15.5 40.4 15.5V15.5"
          stroke="var(--v2-border)"
        />
      </svg>
    </div>

    <!-- Warning header -->
    <div class="v2-price-warning__header">
      <svg
        class="v2-price-warning__icon"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line
          x1="12"
          y1="9"
          x2="12"
          y2="13"
        />
        <line
          x1="12"
          y1="17"
          x2="12.01"
          y2="17"
        />
      </svg>
      <div class="v2-price-warning__message">
        <p class="v2-price-warning__message-text">
          <strong>Some SKUs with updated prices exist in orders from retailers</strong><br>
          Would you like to update prices in these orders?
        </p>
      </div>
      <div class="v2-price-warning__choice-buttons">
        <v2-button
          variant="outline"
          size="sm"
          :class="{ 'v2-price-warning__choice--active': updateOrderPrices === false }"
          @click="handleButtonClick(false)"
        >
          No
        </v2-button>
        <v2-button
          variant="outline"
          size="sm"
          :class="{ 'v2-price-warning__choice--active': updateOrderPrices === true }"
          @click="handleButtonClick(true)"
        >
          Yes, update prices
        </v2-button>
      </div>
    </div>

    <!-- Loading spinner -->
    <div
      v-if="updateOrderPrices && isLoading"
      class="v2-price-warning__loading"
    >
      <v2-spinner size="sm" />
      <span>Loading orders...</span>
    </div>

    <!-- No orders message -->
    <div
      v-if="updateOrderPrices && !isLoading && (!orders || orders.length === 0)"
      class="v2-price-warning__empty"
    >
      <p>No orders found with the updated product prices.</p>
    </div>

    <!-- Orders list section -->
    <div
      v-if="updateOrderPrices && orders && orders.length > 0"
      class="v2-price-warning__orders"
    >
      <div class="v2-price-warning__divider" />
      <h6 class="v2-price-warning__orders-title">
        Updating prices in orders
      </h6>

      <!-- Search input -->
      <div class="v2-price-warning__search">
        <input
          v-model="orderSearchText"
          class="v2-price-warning__search-input"
          placeholder="Search by order id, retailer name, etc."
          @input="filterOrders"
        >
        <svg
          class="v2-price-warning__search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
          />
          <line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          />
        </svg>
      </div>

      <!-- Orders table header -->
      <div class="v2-price-warning__table-header">
        <label class="v2-price-warning__checkbox-wrap">
          <input
            type="checkbox"
            class="v2-price-warning__checkbox"
            :checked="selectAllOrders"
            :indeterminate.prop="isIndeterminate"
            @change="toggleSelectAll($event.target.checked)"
          >
          <span class="v2-price-warning__checkbox-box">
            <svg
              v-if="selectAllOrders"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <svg
              v-else-if="isIndeterminate"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
            >
              <line
                x1="5"
                y1="12"
                x2="19"
                y2="12"
              />
            </svg>
          </span>
        </label>
        <strong class="v2-price-warning__col-order">ORDER ID</strong>
        <strong class="v2-price-warning__col-retailer">RETAILER NAME</strong>
      </div>

      <!-- Orders rows -->
      <div class="v2-price-warning__table-body">
        <div
          v-for="(order, index) in filteredOrders"
          :key="index"
          class="v2-price-warning__row"
        >
          <label class="v2-price-warning__checkbox-wrap">
            <input
              type="checkbox"
              class="v2-price-warning__checkbox"
              :checked="isOrderSelected(order)"
              @change="toggleOrder(order, $event.target.checked)"
            >
            <span class="v2-price-warning__checkbox-box">
              <svg
                v-if="isOrderSelected(order)"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
          </label>
          <div class="v2-price-warning__col-order">
            {{ order.orderSeqId || `Order-${index+1}` }}
          </div>
          <div class="v2-price-warning__col-retailer">
            {{ order.retailerName }}
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="v2-price-warning__actions">
        <v2-button
          variant="outline"
          @click="cancelOrderUpdate"
        >
          Cancel
        </v2-button>
        <v2-button
          :disabled="selectedOrders.length === 0"
          @click="updateSelectedOrders"
        >
          {{ 'Update selected orders' + (selectedOrders.length ? ` (${selectedOrders.length})` : '') }}
        </v2-button>
      </div>
    </div>
  </div>
</template>

<script>
import { GET_PRODUCT_ORDERS, UPDATE_ORDER_PRODUCT_PRICES } from '@/store/modules/productEdit.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import store from '@/store'
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'

export default {
  name: 'V2PriceUpdateWarning',
  components: {
    V2Button,
    V2Spinner,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      updateOrderPrices: null,
      orders: [],
      selectedOrders: [],
      selectAllOrders: false,
      orderSearchText: '',
      filteredOrders: [],
      isLoading: false,
      isHighlighted: false,
    }
  },
  computed: {
    isIndeterminate() {
      return this.selectedOrders.length > 0 && this.selectedOrders.length < this.filteredOrders.length
    },
    productId() {
      return store.state.productEdit.savedState._id
    },
    items() {
      return store.state.productEdit.savedState.items || []
    },
    wholesalePrice() {
      const items = store.state.productEdit.newData?.items
      return items?.[0]?.price ?? null
    },
    retailPrice() {
      const items = store.state.productEdit.newData?.items
      return items?.[0]?.retailPrice ?? null
    },
    originalWholesalePrice() {
      const items = store.state.productEdit.savedState.items
      return items?.[0]?.price ?? null
    },
    originalRetailPrice() {
      const items = store.state.productEdit.savedState.items
      return items?.[0]?.retailPrice ?? null
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.focusAndHighlight()
        })
      } else {
        this.isHighlighted = false
        this.resetState()
      }
    },
    selectedOrders: 'updateSelectAllState',
  },
  methods: {
    resetState() {
      this.updateOrderPrices = null
      this.orders = []
      this.selectedOrders = []
      this.selectAllOrders = false
      this.orderSearchText = ''
      this.filteredOrders = []
    },
    getTrackData() {
      return {
        productId: store.state.productEdit.savedState._id,
        collectionId: store.state.productEdit.savedState.collectionId,
        newPrices: {
          wholesalePrice: this.wholesalePrice,
          retailPrice: this.retailPrice,
        },
        oldPrices: {
          wholesalePrice: this.originalWholesalePrice,
          retailPrice: this.originalRetailPrice,
        },
      }
    },
    focusAndHighlight() {
      if (this.$refs.warningBox) {
        this.$refs.warningBox.focus()
        this.isHighlighted = true
        this.$refs.warningBox.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        setTimeout(() => {
          this.isHighlighted = false
        }, 1500)
      }
    },
    handleButtonClick(val) {
      this.updateOrderPrices = val

      const selectedOption = val ? 'Yes, update prices' : 'No'
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_SELECTS_AN_OPTION_TO_UPDATE, {
        ...this.getTrackData(),
        selectedOption,
      })

      if (val) {
        this.fetchOrdersData()
      } else {
        this.$emit('cancel')
        this.$root.$emit('price-update-closed')
      }
    },
    async fetchOrdersData() {
      if (!this.productId) return

      this.isLoading = true

      try {
        const orders = (await this.$store.dispatch(GET_PRODUCT_ORDERS, {
          productId: this.productId,
        })).filter(({ status }) => !!status)

        this.orders = orders
        this.filteredOrders = orders
        this.selectedOrders = []
        this.selectAllOrders = false
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_FETCHED_AND_DISPLAYED, {
          ...this.getTrackData(),
          orders,
        })
      } catch (error) {
        console.error('Failed to fetch orders:', error)
        this.orders = []
        this.filteredOrders = []
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ERROR_FETCHING_ORDERS, {
          ...this.getTrackData(),
          error,
        })
      } finally {
        this.isLoading = false
      }
    },
    filterOrders() {
      const search = this.orderSearchText.toLowerCase()
      this.filteredOrders = search
        ? this.orders.filter(({ orderSeqId, retailerName }) => orderSeqId?.toLowerCase().includes(search)
          || retailerName?.toLowerCase().includes(search))
        : this.orders
      this.updateSelectAllState()
    },
    isOrderSelected(order) {
      return this.selectedOrders.some(o => o._id === order._id)
    },
    toggleOrder(order, checked) {
      if (checked) {
        this.selectedOrders = [...this.selectedOrders, order]
      } else {
        this.selectedOrders = this.selectedOrders.filter(o => o._id !== order._id)
      }
    },
    toggleSelectAll(checked) {
      this.selectedOrders = checked ? [...this.filteredOrders] : []
    },
    updateSelectAllState() {
      this.selectAllOrders = this.filteredOrders.length > 0
        && this.selectedOrders.length === this.filteredOrders.length

      const trackName = this.selectAllOrders ? 'BRAND_SELECTS_ALL_ORDERS_FOR_UPDATE' : 'BRAND_SELECTS_ORDER_FOR_UPDATE'
      if (this.selectedOrders.length) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT[trackName], {
          ...this.getTrackData(),
          selectedOrders: this.selectedOrders,
        })
      }
    },
    cancelOrderUpdate() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_CANCEL_BUTTON, {
        ...this.getTrackData(),
        selectedOrders: this.selectedOrders,
      })
      this.updateOrderPrices = false
      this.$emit('cancel')
      this.$root.$emit('price-update-closed')
    },
    async updateSelectedOrders() {
      analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_CLICKS_UPDATE_SELECTED_ORDERS_BUTTON, {
        ...this.getTrackData(),
        selectedOrders: this.selectedOrders,
      })
      const payload = this.buildPriceUpdatePayload()
      if (!payload) {
        this.$emit('close')
        return
      }

      try {
        const response = await this.$store.dispatch(UPDATE_ORDER_PRODUCT_PRICES, payload)

        const updatedCount = payload.orderIds.length || 0
        const prices = []
        if (this.wholesalePrice != null && this.wholesalePrice !== this.originalWholesalePrice) prices.push('Wholesale')
        if (this.retailPrice != null && this.retailPrice !== this.originalRetailPrice) prices.push('Retail')

        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_UPDATE_SUCCESS, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          payload,
          response,
        })

        this.$emit('update-orders', this.selectedOrders)
        this.$emit('close')

        setTimeout(() => {
          this.$root.$emit('orders-price-updated', { count: updatedCount, prices })
        }, 100)

        setTimeout(() => {
          this.$root.$emit('price-update-closed')
        }, 2500)
      } catch (err) {
        analytics.track(constants.TRACKS.ACTIONS.PRODUCT_EDIT.BRAND_ORDER_UPDATE_FAILED, {
          productId: store.state.productEdit.savedState._id,
          collectionId: store.state.productEdit.savedState.collectionId,
          payload,
          error: err,
        })
        console.error('Price update failed:', err)
        this.$root.$emit('price-update-closed')
      }
    },
    buildPriceUpdatePayload() {
      const productId = this.productId
      const items = this.items || []
      const wholesale = this.wholesalePrice
      const retail = this.retailPrice

      const hasItems = items.length > 0
      const hasPrice = wholesale != null || retail != null

      if (!productId || !hasItems || !hasPrice) return null

      const toNumber = value => Number(value)

      const mapPriceFields = () => ({
        ...(wholesale != null && { price: toNumber(wholesale) }),
        ...(retail != null && { retailPrice: toNumber(retail) }),
      })

      return {
        orderIds: this.selectedOrders.map(order => order._id),
        products: [
          {
            productId,
            ...mapPriceFields(),
            items: items.map(({ sku }) => ({
              sku,
              ...mapPriceFields(),
            })),
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-price-warning {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--v2-space-4);
  padding: var(--v2-space-4);
  border-radius: var(--v2-radius-md);
  // Glass panel — alert/warning surface in the product edit flow. Top-level
  // within the form, so it matches V2Card variant="panel" treatment.
  border: 1px solid var(--v2-glass-stroke);
  background: var(--v2-glass-surface-1);
  backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
  box-shadow: var(--v2-glass-shadow);
  margin-top: 20px;
  position: relative;
  transition: all 0.3s ease;
  outline: none;

  &--highlight {
    box-shadow: 0 0 0 3px rgba(116, 125, 238, 0.4);
    border-color: var(--v2-primary);
    animation: v2-pulse 1.5s 1;
  }

  &__pointer {
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    width: 41px;
    height: 16px;
    line-height: 0;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    width: 100%;
  }

  &__icon {
    color: var(--v2-warning);
    flex-shrink: 0;
    margin-top: 2px;
  }

  &__message {
    flex: 1;
  }

  &__message-text {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: var(--v2-foreground);

    strong {
      font-weight: 600;
    }
  }

  &__choice-buttons {
    display: flex;
    gap: var(--v2-space-2);
    flex-shrink: 0;
    margin-left: auto;
  }

  &__choice--active {
    background: var(--v2-primary) !important;
    border-color: var(--v2-primary) !important;
    color: var(--v2-primary-foreground) !important;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--v2-space-2);
    padding: var(--v2-space-4);
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    width: 100%;
  }

  &__empty {
    padding: var(--v2-space-4);
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    width: 100%;

    p {
      margin: 0;
    }
  }

  &__orders {
    width: 100%;
  }

  &__divider {
    height: 1px;
    background: var(--v2-border);
    margin: var(--v2-space-4) 0;
  }

  &__orders-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-3);
  }

  &__search {
    position: relative;
    margin-bottom: var(--v2-space-3);
  }

  &__search-input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    padding-right: var(--v2-space-8);
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-sm);
    font-size: 0.8125rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-card);

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }
  }

  &__search-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--v2-muted-foreground);
  }

  &__table-header {
    display: flex;
    align-items: center;
    padding: var(--v2-space-2) 0;
    border-bottom: 1px solid var(--v2-border);
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--v2-muted-foreground);
    gap: var(--v2-space-2);
  }

  &__table-body {
    max-height: 240px;
    overflow-y: auto;
  }

  &__row {
    display: flex;
    align-items: center;
    padding: var(--v2-space-2) 0;
    border-bottom: 1px solid var(--v2-muted);
    font-size: 0.8125rem;
    gap: var(--v2-space-2);
  }

  &__col-order {
    width: 55%;
    padding-right: var(--v2-space-3);
  }

  &__col-retailer {
    width: 40%;
  }

  &__checkbox-wrap {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;
  }

  &__checkbox {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__checkbox-box {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border: 1.5px solid var(--v2-border);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-background);
    color: var(--v2-primary-foreground);
    transition: all 0.15s ease;
  }

  &__checkbox:checked + &__checkbox-box {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
  }

  &__checkbox:indeterminate + &__checkbox-box {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--v2-space-2);
    margin-top: var(--v2-space-4);
  }
}

@keyframes v2-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(116, 125, 238, 0.6);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(116, 125, 238, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(116, 125, 238, 0);
  }
}
</style>
